import React, { useEffect, useState } from "react";
import { Share2, Search, RotateCcw, ArrowUp, ArrowDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface NeighborData {
    id: number;
    protocol: string;
    local_interface_index: number;
    local_interface_name: string | null;
    neighbor_device_id: string;
    neighbor_ip_address: string | null;
    neighbor_port: string | null;
    neighbor_platform: string | null;
    neighbor_capabilities: string | null;
    raw_data: string | null;
    last_seen: string | null;
}

interface DiscoveredNeighborsProps {
    hideHeader?: boolean;
    defaultItemsPerPage?: number;
    compact?: boolean;
    onDataLoaded?: (hasData: boolean) => void;
}

const DiscoveredNeighbors: React.FC<DiscoveredNeighborsProps> = ({
    hideHeader = false,
    defaultItemsPerPage = 10,
    compact = false,
    onDataLoaded
}) => {
    const [data, setData] = useState<NeighborData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = { asset_id: asset_id, client_id: client_id };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { neighbor_device_id: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { local_interface_name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { neighbor_port_id: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/discovered_neighbors", { params });

            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
                if (onDataLoaded) onDataLoaded((response.data.data || []).length > 0);
            } else {
                showError(response.data.message);
                if (onDataLoaded) onDataLoaded(false);
            }
        } catch (error: any) {
            showError(error.message);
            if (onDataLoaded) onDataLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={12} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={12} className="text-orange-500" />
            : <ArrowDown size={12} className="text-orange-500" />;
    };

    return (
        <div className="space-y-4 animate-fade-in">
            {!hideHeader && (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <Share2 className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Discovered Neighbors</h2>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-100 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search neighbors..."
                                className="w-full bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-orange-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => fetchData()}
                            className="p-2 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-500 dark:text-slate-100 hover:text-gray-900 dark:hover:text-white"
                        >
                            <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            )}

            <div className={`bg-white dark:bg-slate-900/50 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm dark:shadow-2xl ${compact ? 'border-orange-500/10' : ''}`}>
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-slate-900/80 border-b border-slate-200 dark:border-slate-800">
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('protocol')}>
                                    <div className="flex items-center gap-1.5">
                                        Protocol
                                        <SortIcon column="protocol" />
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('local_interface_name')}>
                                    <div className="flex items-center gap-1.5">
                                        Local Interface
                                        <SortIcon column="local_interface_name" />
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('neighbor_device_id')}>
                                    <div className="flex items-center gap-1.5">
                                        Neighbor Device ID
                                        <SortIcon column="neighbor_device_id" />
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('neighbor_ip_address')}>
                                    <div className="flex items-center gap-1.5">
                                        Neighbor IP
                                        <SortIcon column="neighbor_ip_address" />
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('neighbor_port')}>
                                    <div className="flex items-center gap-1.5">
                                        Neighbor Port
                                        <SortIcon column="neighbor_port" />
                                    </div>
                                </th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('neighbor_platform')}>
                                    <div className="flex items-center gap-1.5">
                                        Platform
                                        <SortIcon column="neighbor_platform" />
                                    </div>
                                </th>
                                {!compact && (
                                    <>
                                        <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('neighbor_capabilities')}>
                                            <div className="flex items-center gap-1.5">
                                                Capabilities
                                                <SortIcon column="neighbor_capabilities" />
                                            </div>
                                        </th>
                                        <th className="px-4 py-2.5 text-[10px] font-bold text-slate-500 dark:text-slate-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('last_seen')}>
                                            <div className="flex items-center gap-1.5">
                                                Last Seen
                                                <SortIcon column="last_seen" />
                                            </div>
                                        </th>
                                    </>
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={compact ? 6 : 8} className="py-10 text-center">
                                        <RotateCcw className="w-6 h-6 text-orange-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={compact ? 6 : 8} className="py-10 text-center text-slate-500 text-xs">No neighbors found</td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                                        <td className="px-4 py-2 text-[11px] font-medium whitespace-nowrap">
                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${item.protocol === 'lldp'
                                                ? 'bg-blue-500/10 text-blue-600 dark:text-blue-500 border border-blue-500/20'
                                                : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 border border-emerald-500/20'
                                                }`}>
                                                {item.protocol}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-[11px] text-slate-700 dark:text-slate-300 font-bold whitespace-nowrap">
                                            {item.local_interface_name || item.local_interface_index || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-[11px] font-bold text-gray-900 dark:text-white whitespace-nowrap">
                                            {item.neighbor_device_id || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-[11px] text-orange-600 dark:text-orange-400 font-mono whitespace-nowrap">
                                            {item.neighbor_ip_address || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-[11px] text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                            {item.neighbor_port || '-'}
                                        </td>
                                        <td className="px-4 py-2 text-[11px] text-slate-600 dark:text-slate-400 whitespace-nowrap">
                                            {item.neighbor_platform || '-'}
                                        </td>
                                        {!compact && (
                                            <>
                                                <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">
                                                    {item.neighbor_capabilities || '-'}
                                                </td>
                                                <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">
                                                    {item.last_seen || '-'}
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 dark:bg-gray-900/40 px-4 py-1.5 border-t border-slate-200 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={itemsPerPage}
                            onChange={(val) => {
                                setItemsPerPage(val);
                                setCurrentPage(1);
                            }}
                        />
                        <span className="text-[10px] text-slate-500 font-medium whitespace-nowrap">
                            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
                        </span>
                    </div>

                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                            disabled={currentPage === 1}
                            className="p-1 text-slate-400 dark:text-slate-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ArrowUp className="w-3.5 h-3.5 -rotate-90" />
                        </button>
                        <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-2 py-0.5 rounded text-[10px] font-bold">
                            {currentPage}
                        </div>
                        <button
                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                            disabled={currentPage === totalPages || totalPages === 0}
                            className="p-1 text-slate-400 dark:text-slate-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                        >
                            <ArrowDown className="w-3.5 h-3.5 -rotate-90" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiscoveredNeighbors;
